// EAA-1667: One wrapper every app mounts to get the shared core — theme,
// permissions, and the auth capability gate — in a single place. New apps compose
// their own data (a resolved PermissionState from their store/SSO source, plus their
// theme layers/config) and wrap their tree in <CoreProvider>. The auth capability
// `can(key)` is derived from the permission gate here, so an app wires permissions
// once and both the permission hooks and packages/ui's auth-gated atomics resolve
// against the same source.
import React, { useMemo } from 'react';

import { AuthCapabilityProvider, type AuthCapability } from '../auth';
import { PermissionProvider } from '../permission';
import type { PermissionState } from '../permission';
import { CoreThemeProvider, type CoreThemeProviderProps } from '../theme';

/** Theme inputs forwarded to {@link CoreThemeProvider} (app-specific layers/config). */
export type CoreThemeOptions = Omit<CoreThemeProviderProps, 'children'>;

export interface CoreProviderProps {
    children: React.ReactNode;
    /** Resolved permission gate for the current subject (app builds this). */
    permissions: PermissionState;
    /** Theme cascade inputs (app preset/instance, tenant id, antd config helpers). */
    theme: CoreThemeOptions;
    /**
     * Optional override for the auth capability. Defaults to deriving `can(key)`
     * from `permissions.hasCatalogPermission`, so a single permission source backs
     * both the permission hooks and packages/ui's auth-gated components.
     */
    auth?: AuthCapability;
}

/**
 * Composes the core providers in one mount point: theme (outermost), then the
 * permission gate, then the auth capability derived from it. Mount inside the
 * app's store provider, since `permissions`/`theme` are built from app state.
 */
export const CoreProvider: React.FC<CoreProviderProps> = ({
    children,
    permissions,
    theme,
    auth,
}) => {
    const capability = useMemo<AuthCapability>(
        () => auth ?? { can: permissions.hasCatalogPermission },
        [auth, permissions.hasCatalogPermission]
    );

    return (
        <CoreThemeProvider {...theme}>
            <PermissionProvider value={permissions}>
                <AuthCapabilityProvider value={capability}>{children}</AuthCapabilityProvider>
            </PermissionProvider>
        </CoreThemeProvider>
    );
};
